import { useMemo } from 'react';
import type { Contraction, ContractionStats, HospitalRule, EmergencyTrigger } from '../types';
import { calculateContractionStats, checkEmergencyTriggers } from '../utils/calculateStats';
import { checkHospitalRule } from '../utils/checkHospitalRule';

interface UseContractionStatsReturn {
  stats: ContractionStats;
  hospitalRuleCheck: { isMet: boolean; details: string };
  emergencyTriggers: EmergencyTrigger[];
  timeSinceLastContraction: number | null; // in seconds
}

interface UseContractionStatsOptions {
  contractions: Contraction[];
  hospitalRule: HospitalRule;
  maxContractionsForStats?: number;
}

export function useContractionStats({
  contractions,
  hospitalRule,
  maxContractionsForStats = 6,
}: UseContractionStatsOptions): UseContractionStatsReturn {
  // Calculate statistics from contractions
  const stats = useMemo(() => {
    return calculateContractionStats(contractions, maxContractionsForStats);
  }, [contractions, maxContractionsForStats]);

  // Check hospital rule
  const hospitalRuleCheck = useMemo(() => {
    return checkHospitalRule(contractions, hospitalRule);
  }, [contractions, hospitalRule]);

  // Check for emergency triggers
  const emergencyTriggers = useMemo(() => {
    return checkEmergencyTriggers(contractions);
  }, [contractions]);

  // Calculate time since last completed contraction
  const timeSinceLastContraction = useMemo(() => {
    const completedContractions = contractions
      .filter((c) => c.end_time !== null)
      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

    if (completedContractions.length === 0) return null;

    const lastContraction = completedContractions[0];
    const now = Date.now();
    const lastEndTime = new Date(lastContraction.end_time!).getTime();

    return Math.floor((now - lastEndTime) / 1000);
  }, [contractions]);

  return {
    stats,
    hospitalRuleCheck,
    emergencyTriggers,
    timeSinceLastContraction,
  };
}
